import { QueryClient, QueryCache, MutationCache, onlineManager } from '@tanstack/react-query'
import { isQueryCancelledError } from '@appic/lib/http/slErrors'
import { CACHE_MAX_AGE } from './queryPersister'
import { DATA_CLASS } from './dataClasses'

type SentryCapture = (error: unknown, context?: unknown) => void

/**
 * Report a react-query query/mutation error to Sentry (operator directive
 * 2026-06-27: thrown errors must reach Sentry so an agent can triage+fix the
 * recurring classes nonstop). A ContractMismatchError thrown in a queryFn is
 * caught by react-query and held in `query.error` — it surfaces to the user but
 * the RootErrorBoundary (render-only) never sees it, so without this bridge it
 * never reaches Sentry. This shared package can't import @sentry/react, so it
 * uses the `window.__sentryCapture` bridge main.tsx installs (same pattern as
 * PageErrorBoundary).
 *
 * Benign caller-cancellations (observer unmount / route change / strict-mode
 * cleanup) are NOT failures — they were the AbortError storm
 * (BUG-FE-WIRE-UNCAUGHT-ABORTERROR-STORM-2026-06-07) — so they are skipped, the
 * same contract the retry policy below uses.
 */
export function reportQueryError(error: unknown, context: Record<string, unknown>): void {
  if (error instanceof DOMException && error.name === 'AbortError') return
  if (isQueryCancelledError(error)) return
  if (typeof window === 'undefined') return
  const capture = (window as unknown as { __sentryCapture?: SentryCapture }).__sentryCapture
  if (typeof capture !== 'function') return
  const err = error instanceof Error ? error : new Error(String(error))
  const tags: Record<string, string> = { source: 'react-query', errorName: err.name }
  // ContractMismatchError / SLApiError carry a `slug` — tag it so Sentry groups
  // failures by endpoint, letting the triage agent fix the surface per-slug.
  const maybeSlug = (error as { slug?: unknown } | null)?.slug
  if (typeof maybeSlug === 'string') tags.slug = maybeSlug
  capture(err, { tags, contexts: { reactQuery: context } })
}

/**
 * Global online/offline manager.
 *
 * When offline:
 * - All queries are paused (no wasted fetch attempts)
 * - IDB fallback serves cached data via placeholderData
 *
 * When back online:
 * - All stale queries refetch automatically
 * - Supabase Realtime reconnects and fires pending invalidations
 *
 * This is the React Query best practice (same pattern as Linear, Figma).
 * We listen to browser online/offline events to drive it.
 */
onlineManager.setEventListener((setOnline) => {
  const onlineHandler = () => setOnline(true)
  const offlineHandler = () => setOnline(false)
  window.addEventListener('online', onlineHandler)
  window.addEventListener('offline', offlineHandler)
  return () => {
    window.removeEventListener('online', onlineHandler)
    window.removeEventListener('offline', offlineHandler)
  }
})

export const queryClient = new QueryClient({
  // Global error sinks → Sentry. Every query/mutation failure (minus benign
  // cancellations) is reported, so no-dark-surface ContractMismatch errors are
  // triageable from Sentry instead of only living in a component error state.
  queryCache: new QueryCache({
    onError: (error, query) =>
      reportQueryError(error, { queryHash: query.queryHash, queryKey: query.queryKey }),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) =>
      reportQueryError(error, { mutationKey: mutation.options.mutationKey ?? null }),
  }),
  defaultOptions: {
    queries: {
      staleTime: DATA_CLASS.SESSION, // 5min default — override per-query with DATA_CLASS.*
      gcTime: CACHE_MAX_AGE, // 24h — covers full work day + next morning (ADR-037)
      retry: (failureCount, error) => {
        // Never retry aborted requests (React strict mode cleanup, component unmount)
        if (error instanceof DOMException && error.name === 'AbortError') return false
        // Caller-cancellations now surface as a typed QueryCancelledError from
        // the SL wire layer (BUG-FE-WIRE-UNCAUGHT-ABORTERROR-STORM-2026-06-07).
        // Same contract: a cancel is not a failure, never retry it.
        if (isQueryCancelledError(error)) return false
        // Retry once for real errors
        return failureCount < 1
      },
      refetchOnWindowFocus: true,
      networkMode: 'offlineFirst', // serve from cache, refetch in background when online
    },
  },
})
